// ✅ src/components/screens/SellerSubscriptionScreen.js (FULL - NO DEBUG)
// ✅ Reads subscription from: subscriptions/{uid}
// ✅ Fallback: stores/{uid}
// ✅ Shows Status + Paid on + Renew on + Days left
// ✅ FIX: does NOT use storeDoc.isActive for subscription (prevents false Active)
// ✅ Button goes to /seller/pay

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function toMs(ts) {
  try {
    if (!ts) return 0;
    if (ts?.toDate) return ts.toDate().getTime();
    if (typeof ts === "number") return ts;
    if (ts?.seconds != null) return ts.seconds * 1000;
    if (typeof ts === "string") {
      const d = new Date(ts);
      const ms = d.getTime();
      return Number.isFinite(ms) ? ms : 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

function fmtDate(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function fmtDateTime(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

function daysLeftFromMs(ms) {
  if (!ms) return null;
  const diff = ms - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function firstMs(...candidates) {
  for (const c of candidates) {
    const ms = toMs(c);
    if (ms) return ms;
  }
  return 0;
}

export default function SellerSubscriptionScreen({ goBack }) {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  // data sources
  const [subDoc, setSubDoc] = useState(null); // subscriptions/{uid}
  const [storeDoc, setStoreDoc] = useState(null); // stores/{uid} fallback

  // 1) auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
      } else {
        setMsg("");
      }
    });
    return () => unsub();
  }, []);

  // 2) subscriptions realtime (PRIMARY)
  useEffect(() => {
    if (!user?.uid) return;

    setLoading(true);
    setMsg("");

    const ref = doc(db, "subscriptions", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setSubDoc(snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null);
        setLoading(false);
      },
      (err) => {
        setMsg(err?.message || "Failed to load subscription.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  // 3) stores realtime (FALLBACK)
  useEffect(() => {
    if (!user?.uid) return;

    const ref = doc(db, "stores", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => setStoreDoc(snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null),
      () => {}
    );

    return () => unsub();
  }, [user?.uid]);

  // ✅ Use "real subscription payment date" fields first
  const paidAtMs = useMemo(() => {
    return firstMs(
      subDoc?.paidAt,
      subDoc?.paymentDate,
      subDoc?.currentPeriodStart,

      // store fallback fields (your screenshot shows these)
      storeDoc?.subscriptionPaidAt,
      storeDoc?.subscriptionStartedAt,
      storeDoc?.paidAt,
      storeDoc?.activatedAt
    );
  }, [subDoc, storeDoc]);

  // ✅ Use "real subscription expiry" fields first
  const activeUntilMs = useMemo(() => {
    return firstMs(
      subDoc?.currentPeriodEnd,
      subDoc?.activeUntil,
      subDoc?.subscriptionEndsAt,

      // store fallback fields (your screenshot shows these)
      storeDoc?.subscriptionExpiresAt,
      storeDoc?.activeUntil,
      storeDoc?.subscriptionEndsAt,
      storeDoc?.expiresAt
    );
  }, [subDoc, storeDoc]);

  const daysLeft = useMemo(() => daysLeftFromMs(activeUntilMs), [activeUntilMs]);

  // ✅ Decide status WITHOUT storeDoc.isActive (that field is confusing in your DB)
  const isActive = useMemo(() => {
    const s1 = safe(subDoc?.status).toUpperCase();
    const s2 = safe(storeDoc?.subscriptionStatus).toUpperCase();

    // if either doc explicitly says ACTIVE, trust it
    if (s1 === "ACTIVE") return activeUntilMs ? activeUntilMs > Date.now() : true;
    if (s2 === "ACTIVE") return activeUntilMs ? activeUntilMs > Date.now() : true;

    // otherwise, only consider active if there is an expiry date in the future
    // AND we have some payment proof (paid date or payment ref)
    const hasProof =
      !!paidAtMs ||
      !!safe(subDoc?.txRef) ||
      !!safe(subDoc?.paymentRef) ||
      !!safe(storeDoc?.subscriptionLastPaymentRef);

    if (hasProof && activeUntilMs) return activeUntilMs > Date.now();

    return false;
  }, [subDoc, storeDoc, paidAtMs, activeUntilMs]);

  const dueSoon = useMemo(() => {
    if (daysLeft == null) return false;
    return daysLeft <= 3 && daysLeft > 0;
  }, [daysLeft]);

  const expired = useMemo(() => {
    if (daysLeft == null) return false;
    return daysLeft <= 0;
  }, [daysLeft]);

  const statusLabel = useMemo(() => {
    if (!paidAtMs && !activeUntilMs && !subDoc && !storeDoc) return "Not activated";
    if (expired) return "Expired";
    if (isActive) return "Active";

    // if you have store doc but status says inactive
    const s2 = safe(storeDoc?.subscriptionStatus).toUpperCase();
    if (s2 === "INACTIVE") return "Inactive";

    return "Inactive";
  }, [paidAtMs, activeUntilMs, subDoc, storeDoc, expired, isActive]);

  const badgeClass = useMemo(() => {
    if (expired) return "sub-badge sub-badge--bad";
    if (dueSoon) return "sub-badge sub-badge--warn";
    if (isActive) return "sub-badge sub-badge--ok";
    return "sub-badge";
  }, [expired, dueSoon, isActive]);

  const ctaText = useMemo(() => {
    if (!paidAtMs && !activeUntilMs) return "Activate Store";
    if (expired) return "Renew to Reactivate";
    if (dueSoon) return "Renew Now";
    return "Renew Early";
  }, [paidAtMs, activeUntilMs, expired, dueSoon]);

  const dueText = useMemo(() => {
    if (daysLeft == null) return "";
    if (daysLeft <= 0) return "Your subscription has expired.";
    if (daysLeft === 1) return "1 day left until expiry.";
    return `${daysLeft} days left until expiry.`;
  }, [daysLeft]);

  const onPay = () => navigate("/seller/pay");

  return (
    <div style={S.page}>
      <style>{css}</style>

      <div style={S.wrap}>
        <div style={S.top}>
          <button className="sub-back" onClick={goBack ? goBack : () => navigate(-1)}>
            ← Back
          </button>

          <div style={S.topText}>
            <div style={S.h1}>Subscription</div>
            <div style={S.sub}>Manage your monthly store activation.</div>
          </div>

          <button className="sub-pay" onClick={onPay} disabled={!user}>
            {ctaText}
          </button>
        </div>

        {msg ? <div className="sub-msg is-err">{msg}</div> : null}

        <div className="sub-card">
          <div className="sub-row">
            <div>
              <div className="sub-title">Status</div>
              <div className="sub-muted">Your store link depends on subscription.</div>
            </div>

            <span className={badgeClass}>{statusLabel}</span>
          </div>

          <div className="sub-grid">
            <div className="sub-box">
              <div className="sub-box-label">Plan</div>
              <div className="sub-box-value">Monthly</div>
              <div className="sub-box-sub">
                {subDoc?.price != null
                  ? `GHS ${Number(subDoc.price || 0).toLocaleString()} / month`
                  : "GHS 60 / month"}
              </div>
            </div>

            <div className="sub-box">
              <div className="sub-box-label">Paid / Activated on</div>
              <div className="sub-box-value">{fmtDate(paidAtMs)}</div>
              <div className="sub-box-sub">
                {paidAtMs ? `Time: ${fmtDateTime(paidAtMs)}` : "No payment date recorded yet."}
              </div>
            </div>

            <div className="sub-box">
              <div className="sub-box-label">Renew on</div>
              <div className="sub-box-value">{fmtDate(activeUntilMs)}</div>
              <div className="sub-box-sub">{activeUntilMs ? dueText : "Renew date missing."}</div>
            </div>
          </div>

          {loading ? <div className="sub-loading">Loading subscription…</div> : null}

          <div className="sub-actions">
            <button className="sub-pay2" onClick={onPay} disabled={!user}>
              {ctaText}
            </button>

            <button className="sub-soft" onClick={() => navigate("/seller/products")}>
              Go to Products
            </button>
          </div>

          <div className="sub-foot">
            After payment, your store becomes active and your link + preview will work.
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    background: "#f3f6ff",
    padding: 16,
    color: "#0f172a",
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  },
  wrap: { maxWidth: 980, margin: "0 auto", width: "100%" },
  top: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  topText: { minWidth: 220 },
  h1: { fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em" },
  sub: { marginTop: 4, color: "#64748b", fontWeight: 800, fontSize: 12 },
};

const css = `
.sub-back{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:900;
  cursor:pointer;
}
.sub-pay{
  border:0;
  background:#2f61c5;
  color:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:900;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(47,97,197,0.18);
}
.sub-pay:disabled{opacity:.6;cursor:not-allowed;box-shadow:none;}

.sub-msg{
  border:1px solid rgba(255,70,120,0.35);
  background:rgba(255,70,120,0.06);
  color:#b91c1c;
  border-radius:14px;
  padding:12px;
  font-weight:900;
  margin-bottom:12px;
}

.sub-card{
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  padding:14px;
  box-shadow:0 10px 26px rgba(15,23,42,0.06);
  display:grid;
  gap:12px;
}
.sub-row{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:12px;
}
.sub-title{font-weight:900;font-size:16px;}
.sub-muted{margin-top:4px;color:#64748b;font-weight:800;font-size:12px;line-height:1.3;}

.sub-badge{
  border:1px solid #e7ecf6;
  background:#f7fbff;
  border-radius:999px;
  padding:8px 12px;
  font-weight:900;
  font-size:12px;
  color:#334155;
  white-space:nowrap;
}
.sub-badge--ok{background:#f0fdf4;border-color:#bbf7d0;color:#166534;}
.sub-badge--warn{background:#fffbeb;border-color:#fde68a;color:#92400e;}
.sub-badge--bad{background:#fff1f2;border-color:#fecdd3;color:#be123c;}

.sub-grid{
  display:grid;
  grid-template-columns:1fr 1fr 1fr;
  gap:12px;
}
.sub-box{
  border:1px solid #eef2ff;
  background:#fbfdff;
  border-radius:16px;
  padding:12px;
}
.sub-box-label{color:#94a3b8;font-weight:900;font-size:12px;}
.sub-box-value{margin-top:6px;font-size:18px;font-weight:900;}
.sub-box-sub{margin-top:6px;color:#64748b;font-weight:800;font-size:12px;line-height:1.3;}

.sub-loading{
  color:#64748b;
  font-weight:900;
  font-size:12px;
}

.sub-actions{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
}
.sub-pay2{
  border:0;
  background:#2f61c5;
  color:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:900;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(47,97,197,0.18);
}
.sub-pay2:disabled{opacity:.6;cursor:not-allowed;box-shadow:none;}
.sub-soft{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:900;
  cursor:pointer;
}

.sub-foot{
  color:#94a3b8;
  font-weight:800;
  font-size:12px;
  line-height:1.3;
}

@media (max-width: 900px){
  .sub-grid{grid-template-columns:1fr;}
}
`;
